import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

export interface TAndroidKioskLockState {
  /** True when the app is Android Device Owner, which is what arms hard kiosk (direct builds only). */
  deviceOwner: boolean
  /** `ActivityManager.LOCK_TASK_MODE_*`: 0 none, 1 locked (device owner), 2 pinned. */
  lockTaskState: number
}

/**
 * Studio's Android kiosk lock switch (`tenant.androidKioskLockEnabled`, default true). Module-scoped
 * so `applyAndroidKioskLock` stays a no-op the instant the policy turns off, without every caller
 * having to thread the tenant setting through. See `useKioskServiceUnlock` for the sync entry points.
 */
let desiredEnabled = true

export function isAndroidKioskLockDesired(): boolean {
  return desiredEnabled
}

export function setAndroidKioskLockDesired(enabled: boolean): void {
  desiredEnabled = enabled
}

/** Test helper: reset the desired-enabled policy flag between cases. */
export function resetAndroidKioskLockDesiredForTests(): void {
  desiredEnabled = true
}

function parseKioskLockState(raw: unknown): TAndroidKioskLockState | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.deviceOwner !== 'boolean' || typeof o.lockTaskState !== 'number') {
    return null
  }
  return { deviceOwner: o.deviceOwner, lockTaskState: o.lockTaskState }
}

async function invokeKioskCommand(command: 'applyKioskLock' | 'exitKioskLock'): Promise<TAndroidKioskLockState | null> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return parseKioskLockState(await invoke<unknown>(`plugin:lucimark-android|${command}`))
  }
  catch {
    // Plugin bridge may not be ready, or the OEM refused the policy: soft kiosk stays in place.
    return null
  }
}

/**
 * Arms hard kiosk (Lock Task) on Device Owner screens. Idempotent and safe to call unconditionally:
 * a non-Android, non-Tauri or non-device-owner player keeps today's soft kiosk (immersive bars only).
 * No-ops when Studio's kiosk lock switch is off ({@link isAndroidKioskLockDesired}).
 */
export async function applyAndroidKioskLock(): Promise<TAndroidKioskLockState | null> {
  if (!desiredEnabled) {
    return null
  }
  return invokeKioskCommand('applyKioskLock')
}

/**
 * Drops Lock Task for field service. Callers must gate this behind the tenant service PIN
 * (see `useKioskServiceUnlock`) — with one exception: the first-run Accessibility remote-setup
 * screen unlocks so Settings can open while Device Owner Lock Task would otherwise block it.
 */
export async function exitAndroidKioskLock(): Promise<TAndroidKioskLockState | null> {
  return invokeKioskCommand('exitKioskLock')
}
